import React from 'react';

/**
 * Lichess-style auto-shape overlay (engine best-move / threat arrows and PV
 * previews), rendered as an SVG layer above the board.
 *
 * These are "auto" shapes in the lichess sense: computed from the engine, not
 * drawn by the user, so they are pointer-transparent and never persisted.
 * User-drawn right-click shapes keep their existing path through the board
 * package's customArrows and are unaffected by this layer.
 *
 * The brush palette matches the analysis-board conventions users know from
 * lichess: the engine's line is a translucent chunky blue arrow, threat lines
 * are red, and worse multi-line alternatives fade out with the eval gap
 * (via `opacityFactor`).
 */

export type AutoShapeBrush =
  | 'green'
  | 'red'
  | 'blue'
  | 'yellow'
  | 'paleBlue'
  | 'paleGreen'
  | 'paleRed'
  | 'paleGrey';

export interface AutoShape {
  /** Origin square, e.g. "e2". */
  orig: string;
  /** Destination square for arrows; omit for a circle on `orig`. */
  dest?: string;
  brush: AutoShapeBrush;
  /** Multiplier on the brush's base opacity (used to fade worse PV lines). */
  opacityFactor?: number;
}

interface EngineShapesOverlayProps {
  shapes: AutoShape[];
  boardWidth: number;
  orientation: 'white' | 'black';
}

const BRUSHES: Record<
  AutoShapeBrush,
  {color: string; opacity: number; lineWidth: number}
> = {
  green: {color: '#15781B', opacity: 1, lineWidth: 10},
  red: {color: '#882020', opacity: 1, lineWidth: 10},
  blue: {color: '#003088', opacity: 1, lineWidth: 10},
  yellow: {color: '#e68f00', opacity: 1, lineWidth: 10},
  paleBlue: {color: '#003088', opacity: 0.4, lineWidth: 15},
  paleGreen: {color: '#15781B', opacity: 0.4, lineWidth: 15},
  paleRed: {color: '#882020', opacity: 0.4, lineWidth: 15},
  paleGrey: {color: '#4a4a4a', opacity: 0.35, lineWidth: 15},
};

/**
 * Square center in an 8x8 viewBox coordinate space, respecting board
 * orientation (a1 bottom-left for white, top-right for black).
 */
function squareCenter(
  square: string,
  orientation: 'white' | 'black',
): [number, number] | null {
  if (!/^[a-h][1-8]$/.test(square)) {
    return null;
  }
  let file = square.charCodeAt(0) - 97; // 0..7 left→right from white's view
  let rank = parseInt(square[1], 10) - 1; // 0..7 bottom→top from white's view
  if (orientation === 'black') {
    file = 7 - file;
    rank = 7 - rank;
  }
  return [file + 0.5, 7 - rank + 0.5];
}

const EngineShapesOverlay = ({
  shapes,
  boardWidth,
  orientation,
}: EngineShapesOverlayProps) => {
  if (!boardWidth || !shapes?.length) {
    return null;
  }

  const elements: React.ReactNode[] = [];

  shapes.forEach((shape, index) => {
    const brush = BRUSHES[shape.brush] || BRUSHES.paleBlue;
    const opacity =
      brush.opacity * Math.max(0, Math.min(1, shape.opacityFactor ?? 1));
    const from = squareCenter(shape.orig, orientation);
    if (!from) {
      return;
    }

    if (!shape.dest || shape.dest === shape.orig) {
      // Circle on a single square.
      const strokeWidth = 4 / 64;
      elements.push(
        <circle
          key={`shape-${index}`}
          cx={from[0]}
          cy={from[1]}
          r={0.5 - strokeWidth / 2}
          stroke={brush.color}
          strokeWidth={strokeWidth}
          fill="none"
          opacity={opacity}
        />,
      );
      return;
    }

    const to = squareCenter(shape.dest, orientation);
    if (!to) {
      return;
    }

    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const length = Math.hypot(dx, dy);
    if (!length) {
      return;
    }
    const ux = dx / length;
    const uy = dy / length;

    // Shaft width in square units; arrowhead sized off it so pale (wide)
    // and plain (narrow) brushes keep their proportions.
    const strokeWidth = brush.lineWidth / 64;
    // Pull the tip back from the destination square's center a little so
    // stacked arrows into one square stay readable.
    const tipMargin = 10 / 64;
    const headLength = Math.min(2.4 * strokeWidth, length - tipMargin);
    const headHalfWidth = 1.5 * strokeWidth;

    const tipX = to[0] - ux * tipMargin;
    const tipY = to[1] - uy * tipMargin;
    // Shaft stops where the arrowhead base begins.
    const baseX = tipX - ux * headLength;
    const baseY = tipY - uy * headLength;
    // Perpendicular for the head's two back corners.
    const px = -uy;
    const py = ux;

    elements.push(
      <g key={`shape-${index}`} opacity={opacity}>
        <line
          x1={from[0]}
          y1={from[1]}
          x2={baseX}
          y2={baseY}
          stroke={brush.color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <polygon
          points={`${tipX},${tipY} ${baseX + px * headHalfWidth},${
            baseY + py * headHalfWidth
          } ${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`}
          fill={brush.color}
        />
      </g>,
    );
  });

  return (
    <svg
      viewBox="0 0 8 8"
      width={boardWidth}
      height={boardWidth}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 10,
      }}>
      {elements}
    </svg>
  );
};

export default EngineShapesOverlay;
